import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '@/hooks/useAuth';
import { emailValidator } from '@/lib/forms';

export function ForgotPasswordPage() {
    const { sendPasswordReset } = useAuth();

    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const form = useForm({
        defaultValues: {
            email: '',
        },
        onSubmit: async ({ value }) => {
            setError('');
            setSuccess(false);
            setIsLoading(true);

            try {
                await sendPasswordReset(value.email);
                setSuccess(true);
            } catch (err: any) {
                setError(err.message || 'Failed to send reset email');
            } finally {
                setIsLoading(false);
            }
        },
    });

    return (
        <div className="container d-flex justify-content-center align-items-center min-vh-100 py-5">
            <div className="card shadow border-0" style={{ maxWidth: 400, width: '100%' }}>
                <div className="card-body p-4">
                    <div className="text-center mb-4">
                        <h1 className="h3 mb-3 fw-normal">Reset Password</h1>
                        <p className="text-muted">Enter your email to receive a reset link</p>
                    </div>

                    {error && <div className="alert alert-danger" role="alert">{error}</div>}
                    {success && <div className="alert alert-success" role="alert">Check your email for a link to reset your password.</div>}

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <form.Field
                            name="email"
                            validators={{
                                onChange: emailValidator,
                            }}
                        >
                            {(field) => (
                                <div className="mb-3">
                                    <label htmlFor="emailInput" className="form-label">Email address</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        id="emailInput"
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                        placeholder="name@example.com"
                                    />
                                    {field.state.meta.errors.length > 0 && (
                                        <div className="text-danger small mt-1">
                                            {field.state.meta.errors[0]}
                                        </div>
                                    )}
                                </div>
                            )}
                        </form.Field>

                        <button
                            type="submit"
                            className="btn btn-primary w-100 py-2"
                            disabled={isLoading || success}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Sending...
                                </>
                            ) : (
                                'Send Reset Link'
                            )}
                        </button>
                    </form>

                    <div className="text-center mt-3">
                        <Link to="/login" className="text-decoration-none small">
                            <i className="bi bi-arrow-left me-1"></i> Back to Sign In
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
